import { useCallback, useEffect } from "react";

import { useFocusManager } from "@react/hooks/useFocusManager";

import { useLiveRegion } from "./liveRegion";

/**
 * ARIA attributes and utilities for modal/drawer components
 */
export interface ModalAriaProps {
  "aria-describedby"?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-modal": boolean;
  role: "dialog" | "alertdialog";
}

/**
 * Generate ARIA attributes for modal container
 */
export function useModalAria(
  options: {
    describedBy?: string;
    description?: string;
    isAlert?: boolean;
    labelledBy?: string;
    title?: string;
  } = {}
): ModalAriaProps {
  const { describedBy, isAlert = false, labelledBy, title } = options;

  return {
    role: isAlert ? "alertdialog" : "dialog",
    "aria-modal": true,
    ...(labelledBy && { "aria-labelledby": labelledBy }),
    ...(describedBy && { "aria-describedby": describedBy }),
    ...(title && !labelledBy && { "aria-label": title }),
  };
}

/**
 * Hook for managing modal focus and announcements
 */
export function useModalFocus(options: {
  focusManager?: ReturnType<typeof useFocusManager>;
  isOpen: boolean;
  modalName?: string;
  onClose: () => void;
  title?: string;
}) {
  const {
    focusManager: providedFocusManager,
    isOpen,
    modalName,
    onClose,
  } = options;
  const { announceModalState } = useLiveRegion();

  // Always call the hook to avoid conditional hook calls
  const fallbackFocusManager = useFocusManager({
    active: isOpen && !providedFocusManager,
    onActivate: () => {
      if (modalName) {
        announceModalState("opened", modalName);
      }
    },
    onDeactivate: () => {
      if (modalName) {
        announceModalState("closed", modalName);
      }
    },
  });

  // Use provided focus manager if available, otherwise use fallback
  const focusManager = providedFocusManager || fallbackFocusManager;

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      try {
        if (!isOpen) return;

        // Handle Escape key
        if (event.key === "Escape") {
          event.preventDefault();
          onClose();
          return;
        }

        // Handle Tab key trapping
        if (event.key === "Tab") {
          // We need to get the modal element from the DOM since FocusManagerControls doesn't expose containerRef
          const modalElement = document.querySelector(
            '[role="dialog"], [role="alertdialog"]'
          );
          if (!modalElement || !(modalElement instanceof HTMLElement)) return;

          const focusableElements = modalElement.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (
            !(firstElement instanceof HTMLElement) ||
            !(lastElement instanceof HTMLElement)
          )
            return;

          if (event.shiftKey) {
            // Shift + Tab: if focused on first element, move to last
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement?.focus();
            }
          } else {
            // Tab: if focused on last element, move to first
            if (document.activeElement === lastElement) {
              event.preventDefault();
              firstElement?.focus();
            }
          }
        }
      } catch (error) {
        console.warn("Error handling keyboard event:", error);
      }
    },
    [isOpen, onClose]
  );

  // Add keyboard event listener
  useEffect(() => {
    if (isOpen) {
      try {
        document.addEventListener("keydown", handleKeyDown);
        return () => {
          try {
            document.removeEventListener("keydown", handleKeyDown);
          } catch (error) {
            console.warn("Failed to remove keyboard event listener:", error);
          }
        };
      } catch (error) {
        console.warn("Failed to add keyboard event listener:", error);
      }
    }
  }, [isOpen, handleKeyDown]);

  return {
    modalRef: focusManager.setContainer,
    modalProps: {
      tabIndex: -1,
      onKeyDown: handleKeyDown,
    },
    focusManager,
  };
}

/**
 * ARIA attributes for modal backdrop/overlay
 */
export function useModalBackdropAria(): {
  "aria-hidden": boolean;
  tabIndex: number;
} {
  return {
    "aria-hidden": true,
    tabIndex: -1,
  };
}

/**
 * ARIA attributes for modal close button
 */
export function useModalCloseButtonAria(options: {
  modalName?: string;
  title?: string;
}): {
  "aria-label": string;
} {
  const { modalName, title } = options;

  const label = title
    ? `Close ${title}`
    : modalName
    ? `Close ${modalName}`
    : "Close dialog";

  return {
    "aria-label": label,
  };
}
